import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { User, Lock, FileText, ArrowLeft, Loader2, CheckCircle2, Clock, AlertCircle, Phone, MapPin, School, X, GraduationCap, MessageCircle } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';

const GOVERNORATES = ["الدقهلية", "القاهرة", "الجيزة", "الإسكندرية", "القليوبية", "الشرقية", "المنوفية", "الغربية", "دمياط", "كفر الشيخ", "البحيرة", "الإسماعيلية", "بورسعيد", "السويس", "مطروح", "شمال سيناء", "جنوب سيناء", "بني سويف", "الفيوم", "المنيا", "أسيوط", "سوهاج", "قنا", "الأقصر", "أسوان", "الوادي الجديد", "البحر الأحمر"];

const STUDY_BRANCHES = [
  { value: 'scientific', label: 'علمي' },
  { value: 'literary', label: 'أدبي' }
];

export const Login = () => {
  const location = useLocation();
  const initialTab = new URLSearchParams(location.search).get('tab');
  const [isLogin, setIsLogin] = useState(initialTab !== 'register');
  const [loading, setLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState({ type: '', text: '', code: '' });
  const [showFloatingAlert, setShowFloatingAlert] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [showDeviceSwitchModal, setShowDeviceSwitchModal] = useState(false);
  const [deviceSwitchLoading, setDeviceSwitchLoading] = useState(false);
  
  const [phoneCode, setPhoneCode] = useState('+20');
  const [fatherPhoneCode, setFatherPhoneCode] = useState('+20');
  const [motherPhoneCode, setMotherPhoneCode] = useState('+20');

  const COUNTRY_CODES = [
    { code: '+20', label: 'جمهورية مصر العربية (+20)' },
    { code: '+966', label: 'المملكة العربية السعودية (+966)' },
    { code: '+971', label: 'الإمارات العربية المتحدة (+971)' },
    { code: '+965', label: 'دولة الكويت (+965)' },
  ];

  const { signIn, register, user, profile } = useAuth();
  const navigate = useNavigate();

  // Device ID generation
  const getDeviceId = () => {
    let id = localStorage.getItem('atfy_device_id');
    if (!id) {
      id = crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substring(2, 15);
      localStorage.setItem('atfy_device_id', id);
    }
    return id;
  };
  
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    password: '',
    bio: '',
    school: '',
    father_phone: '',
    mother_phone: '',
    governorate: '',
    grade: '2sec',
    study_branch: 'scientific'
  });

  useEffect(() => {
    if (user && profile) {
      navigate('/');
    }
  }, [user, profile, navigate]);

  const handleSubmit = async (e) => {
    if (e) e.preventDefault(); 
    setLoading(true);
    setStatusMessage({ type: '', text: '', code: '' });
    setShowFloatingAlert(false);

    if (!isLogin && /[0-9]/.test(formData.fullName)) {
      setStatusMessage({ type: 'error', text: 'لا يُسمح بالأرقام في الاسم. يرجى كتابة الاسم بالعربية.' });
      setLoading(false);
      return;
    }

    if (!isLogin && formData.fullName.length > 30) {
      setStatusMessage({ type: 'error', text: 'الاسم طويل جداً، يرجى الاختصار لـ 30 حرف.' });
      setLoading(false);
      return;
    }
    
    try {
      if (isLogin) {
        if (!formData.phone || !formData.password) {
          setStatusMessage({ type: 'error', text: 'يرجى إدخال رقم الموبايل وكلمة المرور.' });
          setLoading(false);
          return;
        }
        const result = await signIn(formData.phone.trim(), formData.password, getDeviceId());
        if (result.success) {
          navigate('/');
          return;
        }
        
        const errStr = (result?.error || '').toString().toLowerCase();
        if (result?.error === 'ACCOUNT_KICKED') {
          setStatusMessage({ type: 'error', text: result.message || 'تم طردك من المنصة نهائياً.' });
        } else if (errStr.includes('account_pending') || errStr.includes('مراجعة')) {
          setStatusMessage({ type: 'pending', text: 'حسابك في مرحلة المراجعة.. سنقوم بتفعيله في أقرب وقت.' });
        } else if (errStr.includes('device_locked') || result?.error === 'DEVICE_LOCKED') {
          setShowDeviceSwitchModal(true);
        } else {
          setShowFloatingAlert(true);
        }
        
      } else {
        if (!formData.fullName.trim() || !formData.phone.trim() || !formData.password.trim() || !formData.governorate || !formData.grade || !formData.study_branch) {
          setStatusMessage({ type: 'error', text: 'يرجى إكمال جميع البيانات الأساسية.' });
          setLoading(false);
          return;
        }

        if (!formData.father_phone.trim() && !formData.mother_phone.trim()) {
          setStatusMessage({ type: 'error', text: 'لا بد من إدخال رقم ولي أمر واحد على الأقل.' });
          setLoading(false);
          return;
        }

        setShowConfirmModal(true);
        setLoading(false);
        return;
      }
    } catch (error) {
      if (isLogin) {
        setShowFloatingAlert(true);
      } else {
        setStatusMessage({ type: 'error', text: 'حدث خطأ في الاتصال، يرجى المحاولة مرة أخرى.' });
      }
    } finally { setLoading(false); }
  };

  const handleDeviceSwitch = async () => {
    setDeviceSwitchLoading(true);
    try {
      const { switchDeviceLogin } = await import('../../services/d1Api');
      const result = await switchDeviceLogin({
        phone: formData.phone.trim(),
        password: formData.password,
        device_id: getDeviceId()
      });
      if (result.success) {
        localStorage.setItem('auth_token', result.token);
        localStorage.setItem('userId', result.user.id);
        setShowDeviceSwitchModal(false);
        window.location.href = '/';
      } else {
        setStatusMessage({ type: 'error', text: result.error || 'فشل تبديل الجهاز' });
        setShowDeviceSwitchModal(false);
      }
    } catch (err) {
      setStatusMessage({ type: 'error', text: 'حدث خطأ أثناء تبديل الجهاز' });
      setShowDeviceSwitchModal(false);
    } finally {
      setDeviceSwitchLoading(false);
    }
  };

  const confirmRegistration = async () => {
    if (loading) return;
    setLoading(true);
    setStatusMessage({ type: '', text: '', code: '' });

    const formattedFatherPhone = formData.father_phone.trim() ? `(${fatherPhoneCode}) ${formData.father_phone.trim()}` : '';
    const formattedMotherPhone = formData.mother_phone.trim() ? `(${motherPhoneCode}) ${formData.mother_phone.trim()}` : '';
    const studentEmail = `${formData.phone.trim().replace(/\s/g, '')}@sabeel.com`;

    const nameParts = formData.fullName.trim().split(/\s+/);
    const fName = nameParts[0] || '';
    const mName = nameParts.length > 2 ? nameParts.slice(1, -1).join(' ') : '';
    const lName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : 'طالب';

    try {
      const result = await register(
        studentEmail,
        formData.password,
        formData.fullName,
        {
          firstName: fName,
          middleName: mName,
          lastName: lName,
          bio: formData.bio || '',
          school: formData.school || '',
          phone: formData.phone.trim(),
          father_phone: formattedFatherPhone,
          mother_phone: formattedMotherPhone,
          governorate: formData.governorate,
          grade: formData.grade,
          study_branch: formData.study_branch,
          device_id: getDeviceId()
        }
      );

      if (result.success) {
        setShowConfirmModal(false);
        setIsLogin(true);
        setStatusMessage({ type: 'success', text: 'تم إرسال بياناتك بنجاح! سيتم مراجعة حسابك وتفعيله في أقرب وقت.' });
      } else {
        setStatusMessage({ type: 'error', text: result.error || 'فشل التسجيل، الرقم مستخدم مسبقاً.' });
        setShowConfirmModal(false);
      }
    } catch (error) {
      setStatusMessage({ type: 'error', text: 'حصلت مشكلة فنية، حاول تاني.' });
      setShowConfirmModal(false);
    } finally {
      setLoading(false);
    }
  };

  // Shared input classes
  const inputClass = "w-full bg-[#F8F8F8] border-2 border-[#1A1A2E]/15 rounded-xl py-3.5 pr-11 pl-4 text-sm focus:border-[#1A1A2E] focus:bg-white outline-none text-right transition-all placeholder:text-[#1A1A2E]/40";
  const selectClass = "w-full bg-[#F8F8F8] border-2 border-[#1A1A2E]/15 rounded-xl py-3.5 pr-11 pl-4 text-xs focus:border-[#1A1A2E] focus:bg-white outline-none text-right appearance-none transition-all";
  const iconClass = "absolute right-3.5 top-1/2 -translate-y-1/2 text-[#1A1A2E]/50";

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 md:p-8"
      dir="rtl"
      style={{ background: '#FFFFFF', backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200' viewBox='0 0 200 200'%3E%3Cg fill='none' stroke='%231A1A2E' stroke-width='1.2' opacity='0.07'%3E%3Ccircle cx='30' cy='30' r='7'/%3E%3Cellipse cx='30' cy='30' rx='18' ry='7'/%3E%3C/g%3E%3C/svg%3E\")", backgroundRepeat: 'repeat' }}
    >

      {/* ====== Custom Confirmation Modal ====== */}
      {showConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-md" onClick={() => setShowConfirmModal(false)}></div>
          <div className="relative bg-white border border-gray-200 rounded-2xl p-6 w-full max-w-md shadow-2xl">
            <h3 className="text-xl font-black mb-4 text-center text-[#1A1A2E]">تأكيد البيانات</h3>
            <div className="bg-gray-50 rounded-xl p-4 mb-6 space-y-3 text-sm border border-gray-100">
              <div className="flex justify-between border-b border-gray-100 pb-2"><span className="text-gray-500">الاسم:</span><span className="font-bold text-gray-800">{formData.fullName}</span></div>
              <div className="flex justify-between border-b border-gray-100 pb-2"><span className="text-gray-500">الموبايل:</span><span className="font-bold text-left text-gray-800" dir="ltr">{phoneCode} {formData.phone}</span></div>
              <div className="flex justify-between border-b border-gray-100 pb-2"><span className="text-gray-500">الصف:</span><span className="font-bold text-gray-800">{formData.grade === '1sec' ? 'أولى ثانوي' : formData.grade === '2sec' ? 'تانية ثانوي' : 'تالتة ثانوي'}</span></div>
              <div className="flex justify-between border-b border-gray-100 pb-2"><span className="text-gray-500">الشعبة:</span><span className="font-bold text-gray-800">{formData.study_branch === 'scientific' ? 'علمي' : 'أدبي'}</span></div>
              <div className="flex justify-between border-b border-gray-100 pb-2"><span className="text-gray-500">موبايل الأب:</span><span className="font-bold text-left text-gray-800" dir="ltr">{fatherPhoneCode} {formData.father_phone}</span></div>
            </div>
            <div className="flex gap-3">
              <button onClick={confirmRegistration} disabled={loading} className="flex-1 bg-[#1A1A2E] hover:bg-[#2D2D44] text-white font-black py-3 px-4 rounded-xl transition flex items-center justify-center shadow-lg">
                {loading ? <Loader2 className="animate-spin" size={20} /> : 'تأكيد'}
              </button>
              <button onClick={() => setShowConfirmModal(false)} disabled={loading} className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold py-3 px-4 rounded-xl transition">
                مراجعة
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ====== Floating 'No Account' Toast Alert ====== */}
      {showFloatingAlert && (
        <div className="fixed top-6 left-1/2 z-50 w-[92%] max-w-md -translate-x-1/2 animate-slide-up">
          <div className="relative rounded-2xl p-5 shadow-2xl" style={{ background: 'linear-gradient(145deg,#252540,#1A1A2E)', border: '1px solid rgba(201,168,76,0.3)' }}>
            <button onClick={() => setShowFloatingAlert(false)} className="absolute top-3 left-3 text-white/50 hover:text-white transition-colors">
              <X size={18} />
            </button>
            <div className="flex items-start gap-3">
              <AlertCircle className="text-red-400 shrink-0 mt-0.5 animate-pulse" size={26} />
              <div className="space-y-3">
                <p className="text-sm font-bold leading-relaxed text-white">عفواً يا بطل، الرقم أو الباسورد غلط، أو الرقم ده ملوش حساب عندنا.. لو لسه مسجلتش، اعمل حساب جديد عشان تنورنا.</p>
                <button onClick={() => { setShowFloatingAlert(false); setIsLogin(false); }} className="w-full font-black text-xs py-3 px-6 rounded-xl transition-all flex items-center justify-center gap-2 shadow-lg" style={{ background: '#C9A84C', color: '#1A1A2E' }}>
                  <User size={16} /> إنشاء حساب جديد
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Container */}
      <div className="relative z-10 w-full max-w-5xl flex flex-col md:flex-row rounded-3xl overflow-hidden border border-gray-200 shadow-2xl bg-white">
        
        {/* Right Side — Purple brand panel, NO photo */}
        <div className="hidden md:flex md:w-1/2 relative flex-col items-center justify-center overflow-hidden text-center px-10" style={{ background: 'linear-gradient(160deg, #1A1A2E 0%, #2D2D44 100%)' }}>

          {/* Subtle pattern overlay */}
          <div className="absolute inset-0 opacity-5 pointer-events-none" style={{
            backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Ccircle cx='20' cy='20' r='8' fill='none' stroke='white' stroke-width='1'/%3E%3Ccircle cx='80' cy='60' r='6' fill='none' stroke='white' stroke-width='1'/%3E%3Cpath d='M50 10 L55 25 L70 25 L58 33 L63 48 L50 40 L37 48 L42 33 L30 25 L45 25 Z' fill='none' stroke='white' stroke-width='1'/%3E%3C/svg%3E\")",
            backgroundRepeat: 'repeat'
          }} />

          {/* Logo circle */}
          <div className="w-24 h-24 rounded-full flex items-center justify-center mb-6 shadow-2xl" style={{ background: '#C9A84C' }}>
            <img src="/images/logo.png" alt="" className="w-16 h-16 object-contain"
              onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.innerHTML = '<span style="font-size:2.5rem;font-weight:900;color:#1A1A2E">أ</span>'; }} />
          </div>

          <h2 className="text-4xl md:text-5xl font-black text-white mb-3 leading-tight">
            منصة أستاذ
          </h2>
          <h3 className="text-3xl font-black mb-4" style={{ color: '#C9A84C' }}>محمد العاطفي</h3>
          <div className="inline-block px-5 py-2 rounded-lg mb-8" style={{ background: '#C9A84C' }}>
            <p className="text-[#1A1A2E] font-bold text-sm">لغة إنجليزية</p>
          </div>

          {/* Decorative dots */}
          <div className="flex gap-2 justify-center">
            <div className="w-2 h-2 rounded-full opacity-40" style={{ background: '#C9A84C' }}></div>
            <div className="w-2 h-2 rounded-full opacity-70" style={{ background: '#C9A84C' }}></div>
            <div className="w-2 h-2 rounded-full" style={{ background: '#C9A84C' }}></div>
            <div className="w-2 h-2 rounded-full opacity-70" style={{ background: '#C9A84C' }}></div>
            <div className="w-2 h-2 rounded-full opacity-40" style={{ background: '#C9A84C' }}></div>
          </div>
        </div>

        {/* Form Side */}
        <div className="w-full md:w-1/2 bg-white p-8 md:p-12 relative overflow-hidden flex flex-col justify-center">

          {/* Mobile header */}
          <div className="md:hidden text-center mb-8 relative z-10">
            <h1 className="text-3xl font-black text-[#1A1A2E] mb-1">
              منصة أ. <span style={{ color: '#C9A84C' }}>محمد العاطفي</span>
            </h1>
          </div>

          {/* Login/Register Toggle */}
          <div className="flex bg-gray-100 p-1.5 rounded-xl mb-8 relative z-10 shrink-0">
            <button type="button" onClick={() => setIsLogin(true)} className={`flex-1 py-3 rounded-lg text-sm font-black transition-all ${isLogin ? 'bg-[#1A1A2E] text-white shadow-lg' : 'text-gray-500 hover:text-gray-800'}`}>
              دخول الطلاب
            </button>
            <button type="button" onClick={() => setIsLogin(false)} className={`flex-1 py-3 rounded-lg text-sm font-black transition-all ${!isLogin ? 'bg-[#1A1A2E] text-white shadow-lg' : 'text-gray-500 hover:text-gray-800'}`}>
              إنشاء حساب
            </button>
          </div>

          {/* Status Messages */}
          {statusMessage.text && (
            <div className={`relative z-10 mb-6 p-4 rounded-xl border flex items-start gap-3 shadow-sm animate-fade-in shrink-0 ${
              statusMessage.type === 'success' ? 'bg-green-50 border-green-200 text-green-700' :
              statusMessage.type === 'pending' ? 'bg-yellow-50 border-yellow-200 text-yellow-700' :
              'bg-red-50 border-red-200 text-red-700'
            }`}>
              {statusMessage.type === 'success' ? <CheckCircle2 size={18} className="shrink-0 mt-0.5" /> :
               statusMessage.type === 'pending' ? <Clock size={18} className="shrink-0 mt-0.5" /> : <AlertCircle size={18} className="shrink-0 mt-0.5" />}
              <p className="text-xs font-bold leading-relaxed">{statusMessage.text}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4 max-h-[60vh] md:max-h-[500px] overflow-y-auto pr-1 relative z-10 scroll-smooth pb-4">
            
            {!isLogin && (
              <div className="relative group">
                <User className={iconClass} size={18} />
                <input type="text" placeholder="الاسم الرباعي (باللغة العربية)" required maxLength={30} className={inputClass} value={formData.fullName} onChange={(e) => setFormData({...formData, fullName: e.target.value})} />
              </div>
            )}

            {/* Phone */}
            <div className="relative group flex flex-col gap-3">
              <div className="relative w-full">
                <MapPin className={iconClass} size={17} />
                <select className={selectClass} value={phoneCode} onChange={(e) => setPhoneCode(e.target.value)} dir="rtl">
                  {COUNTRY_CODES.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
                </select>
              </div>
              <div className="relative w-full">
                <Phone className={iconClass} size={18} />
                <input type="tel" maxLength={20} placeholder="رقم الموبايل" required className={`${inputClass} text-left`} value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} dir="ltr" />
              </div>
            </div>

            {!isLogin && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div className="relative">
                    <FileText className={iconClass} size={17} />
                    <select required className={selectClass} value={formData.grade} onChange={(e) => setFormData({ ...formData, grade: e.target.value })}>
                      <option value="1sec">أولى ثانوي</option>
                      <option value="2sec">تانية ثانوي</option>
                      <option value="3sec">تالتة ثانوي</option>
                    </select>
                  </div>
                  <div className="relative">
                    <GraduationCap className={iconClass} size={17} />
                    <select required className={selectClass} value={formData.study_branch} onChange={(e) => setFormData({ ...formData, study_branch: e.target.value })}>
                      {STUDY_BRANCHES.map(branch => <option key={branch.value} value={branch.value}>{branch.label}</option>)}
                    </select>
                  </div>
                </div>

                {/* Parent Phones */}
                <p className="text-xs text-[#1A1A2E] font-bold p-2 rounded-lg bg-[#1A1A2E]/5 border border-[#1A1A2E]/10">
                  ملاحظة: في حال عدم وجود رقم لأحد الأب أو الأم يرجى ترك المكان فاضي.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-1">
                  <div className="relative group flex flex-col gap-2">
                    <select className={selectClass + ' pr-3'} value={fatherPhoneCode} onChange={(e) => setFatherPhoneCode(e.target.value)} dir="rtl">
                      {COUNTRY_CODES.map(c => <option key={c.code} value={c.code}>{c.code}</option>)}
                    </select>
                    <div className="relative flex-1">
                        <Phone className={iconClass} size={16} />
                        <input type="tel" maxLength={20} placeholder="موبايل الأب" required className={`${inputClass} pr-10`} value={formData.father_phone} onChange={(e) => setFormData({...formData, father_phone: e.target.value})} dir="ltr"/>
                    </div>
                  </div>
                  <div className="relative group flex flex-col gap-2">
                    <select className={selectClass + ' pr-3'} value={motherPhoneCode} onChange={(e) => setMotherPhoneCode(e.target.value)} dir="rtl">
                      {COUNTRY_CODES.map(c => <option key={c.code} value={c.code}>{c.code}</option>)}
                    </select>
                    <div className="relative flex-1">
                        <Phone className={iconClass} size={16} />
                        <input type="tel" maxLength={20} placeholder="موبايل الأم" className={`${inputClass} pr-10`} value={formData.mother_phone} onChange={(e) => setFormData({...formData, mother_phone: e.target.value})} dir="ltr"/>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 pt-1">
                  <div className="relative">
                    <MapPin className={iconClass} size={17} />
                    <select required className={selectClass} value={formData.governorate} onChange={(e) => setFormData({...formData, governorate: e.target.value})}>
                      <option value="">المحافظة</option>
                      {GOVERNORATES.map(gov => <option key={gov} value={gov}>{gov}</option>)}
                    </select>
                  </div>
                  <div className="relative">
                    <School className={iconClass} size={17} />
                    <input type="text" placeholder="اسم المدرسة (اختياري)" className={`${inputClass} text-xs pr-11`} value={formData.school} onChange={(e) => setFormData({...formData, school: e.target.value})} />
                  </div>
                </div>
              </>
            )}

            {/* Password */}
            <div className="relative group border-t border-gray-100 pt-4">
              <Lock className={iconClass} size={18} />
              <input type="password" placeholder="كلمة المرور" required className={inputClass} value={formData.password} onChange={(e) => setFormData({...formData, password: e.target.value})} />
            </div>

            {/* Forgot Password */}
            {isLogin && (
              <button type="button" onClick={() => setShowForgotPasswordModal(true)}
                className="text-[#1A1A2E] hover:text-[#2D2D44] text-sm font-bold transition block w-full text-center mt-1">
                نسيت كلمة المرور؟
              </button>
            )}

            <button type="submit" disabled={loading}
              className="w-full bg-[#1A1A2E] hover:bg-[#2D2D44] text-white font-black py-4 mt-6 rounded-xl transition-all flex items-center justify-center gap-3 disabled:opacity-50 shadow-lg hover:shadow-xl hover:-translate-y-0.5 active:scale-[0.98] shrink-0">
              {loading ? <Loader2 className="animate-spin" size={20} /> : (
                <><ArrowLeft size={20} /><span>{isLogin ? 'دخول آمن للمنصة' : 'إنشاء حساب جديد'}</span></>
              )}
            </button>
          </form>
        </div>
      </div>

      {/* ====== Forgot Password Modal ====== */}
      {showForgotPasswordModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setShowForgotPasswordModal(false)}>
          <div className="absolute inset-0 bg-black/70 backdrop-blur-md"></div>
          <div className="relative rounded-2xl p-6 w-full max-w-md shadow-2xl z-10" style={{ background: 'linear-gradient(145deg,#252540,#1A1A2E)', border: '1px solid rgba(201,168,76,0.3)', color: '#fff' }} onClick={e => e.stopPropagation()}>
            <button onClick={() => setShowForgotPasswordModal(false)} className="absolute top-4 left-4 text-white/50 hover:text-white transition-colors">
              <X size={20} />
            </button>

            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg" style={{ background: '#C9A84C' }}>
                <MessageCircle size={32} style={{ color: '#1A1A2E' }} />
              </div>
              <h3 className="text-xl font-black text-white mb-2">نسيت كلمة المرور؟</h3>
              <p className="text-sm text-white/60">تواصل مع الدعم الفني عبر واتساب وسيتم مساعدتك في استعادة حسابك</p>
            </div>

            <div className="space-y-3">
              <a href="https://wa.me/201028608446?text=السلام عليكم، نسيت كلمة المرور الخاصة بحسابي على منصة أستاذ محمد العاطفي، أرجو المساعدة."
                target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-3 w-full bg-green-600 hover:bg-green-700 text-white font-bold py-4 px-5 rounded-xl transition-all shadow-lg">
                <MessageCircle size={22} className="shrink-0" />
                <span className="text-sm">تواصل مع الدعم الفني (1)</span>
              </a>
              <a href="https://wa.me/201064756266?text=السلام عليكم، نسيت كلمة المرور الخاصة بحسابي على منصة أستاذ محمد العاطفي، أرجو المساعدة."
                target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-3 w-full bg-green-600 hover:bg-green-700 text-white font-bold py-4 px-5 rounded-xl transition-all shadow-lg">
                <MessageCircle size={22} className="shrink-0" />
                <span className="text-sm">تواصل مع الدعم الفني (2)</span>
              </a>
            </div>

            <button onClick={() => setShowForgotPasswordModal(false)}
              className="w-full mt-4 py-3 font-bold rounded-xl transition"
              style={{ background: 'rgba(255,255,255,0.08)', color: '#fff', border: '1px solid rgba(201,168,76,0.2)' }}>
              إغلاق
            </button>
          </div>
        </div>
      )}
      {/* Device Switch Confirmation Modal */}
      {showDeviceSwitchModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-6" style={{ background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)' }}>
          <div className="w-full max-w-sm rounded-2xl p-6 text-center" style={{ background: '#0F0F1A', border: '2px solid rgba(201,168,76,0.3)' }}>
            <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: 'rgba(201,168,76,0.15)' }}>
              <span className="text-3xl">📱</span>
            </div>
            <h3 className="text-xl font-black mb-3" style={{ color: '#C9A84C' }}>تبديل الجهاز</h3>
            <p className="text-white/80 text-sm mb-2 leading-relaxed">
              هذا الحساب مسجل على جهاز آخر.
            </p>
            <p className="text-white/60 text-xs mb-6 leading-relaxed">
              لو عايز تسجل دخول من الجهاز ده، هيتم تسجيل خروجك من الجهاز القديم تلقائياً.
            </p>
            <div className="space-y-3">
              <button
                onClick={handleDeviceSwitch}
                disabled={deviceSwitchLoading}
                className="w-full py-3 rounded-xl font-black text-sm transition-all hover:scale-[1.02]"
                style={{ background: '#C9A84C', color: '#0F0F1A' }}
              >
                {deviceSwitchLoading ? '⏳ جاري التبديل...' : '✅ تأكيد التبديل'}
              </button>
              <button
                onClick={() => setShowDeviceSwitchModal(false)}
                className="w-full py-3 rounded-xl font-bold text-sm text-white/60 hover:text-white transition"
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
              >
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
